import type * as openapi from '@/helpers/api/types/openapiOverrides';
import type { TimeObject } from '@/models/jobEdit';

export type StopTimeObject = TimeObject;

export interface StopHint {
  text: string;
  type: 'info' | 'warning';
}

export type StopTimeMode = 'any' | 'ampm' | 'specific';
export type StopAmPm = 'AM' | 'PM' | null;

export type StopNumber = 1 | 2 | 3;

export interface BookingStop {
  number: StopNumber;
  name: string;
  address_line1: string;
  address_line2: string;
  suburb: string;
  suburb_id: number | null;
  state: string;
  postcode: string;
  warehouse: boolean;
  attributes: string[];
  note_name: string;
  note_phone: string;
  note_text: string;
  note_email: string;
  start_date: string | null;
  end_date: string | null;
  link_dates: boolean;
  time_mode: StopTimeMode;
  am_pm: StopAmPm;
  time_from: StopTimeObject | null;
  time_to: StopTimeObject | null;
  save_to_address_book: boolean;
}

export interface BookingProduct {
  serial: string;
  model: string;
  model_id: number | null;
  description: string;
  quantity: number;
  weight: number;
  from_stop: StopNumber;
  to_stop: StopNumber;
  installation: openapi.PostJobBookApi_Body[number]['products'][number]['installation'];
  runup: boolean;
  returns: openapi.PostJobBookApi_Body[number]['products'][number]['returns'];
  finisher: boolean;
  predefined_workshop: boolean;
  custom_workshop: boolean;
  predefined_workshop_tasks?: string[];
  custom_workshop_task?: string;
}

export interface JobBookingCustomer {
  customer_code: string | null;
  caller: string;
  phone: string;
  REF1: string;
  REF2: string;
  REF3: string;
}

export interface JobBookingRefConfig {
  label: string;
  required: boolean;
}

export type BookingDateTimeModel = Pick<
  BookingStop,
  | 'start_date'
  | 'end_date'
  | 'link_dates'
  | 'time_mode'
  | 'am_pm'
  | 'time_from'
  | 'time_to'
>;

export function createEmptyCustomer(): JobBookingCustomer {
  return {
    customer_code: null,
    caller: '',
    phone: '',
    REF1: '',
    REF2: '',
    REF3: '',
  };
}

export function createEmptyBookingProduct(
  fromStop: StopNumber = 1,
  toStop: StopNumber = 2,
): BookingProduct {
  return {
    serial: '',
    model: '',
    model_id: null,
    description: '',
    quantity: 1,
    weight: 0,
    from_stop: fromStop,
    to_stop: toStop,
    installation: '',
    runup: false,
    returns: '',
    finisher: false,
    predefined_workshop: false,
    custom_workshop: false,
  };
}

export function createEmptyStop(number: StopNumber): BookingStop {
  return {
    number,
    name: '',
    address_line1: '',
    address_line2: '',
    suburb: '',
    suburb_id: null,
    state: '',
    postcode: '',
    warehouse: false,
    attributes: [],
    note_name: '',
    note_phone: '',
    note_text: '',
    note_email: '',
    start_date: null,
    end_date: null,
    link_dates: false,
    time_mode: 'any',
    am_pm: null,
    time_from: null,
    time_to: null,
    save_to_address_book: false,
  };
}
